import { HAP_TO_VND, LOYALTY_APPLY_SCOPE, REWARD_UNIT } from "../core/constants";
import { computeLoyaltyStatus, formatRewardAmount, mapRewardAmountToAwardValue } from "../core/utils";

/** BE LoyaltyPackageAwardUnit → FE rewardUnit */
export function mapAwardUnitToRewardUnit(awardUnit) {
  if (awardUnit === "MONEY_PER_ITEM") return REWARD_UNIT.PER_PRODUCT;
  if (awardUnit === "MONEY_PER_AWARD") return REWARD_UNIT.PER_MILESTONE;
  if (awardUnit === "LOYALTY_POINT") return REWARD_UNIT.HAP;
  if (awardUnit === "GIFT_PER_AWARD") return REWARD_UNIT.GIFT_PER_AWARD;
  return awardUnit;
}

/** ID ổn định theo thứ tự mảng — tránh trùng khi BE dùng position 0-based lẫn fallback index+1 */
export function milestoneIdAt(index) {
  return `ms-idx-${index}`;
}

/** BE award → FE rewardAmount (Giá trị thưởng = awardValue; legacy Tiền/item: awardAmount/minItems) */
function mapBeAwardToRewardAmount(item) {
  const award = item?.award;
  if (!award) return undefined;

  const unit = mapAwardUnitToRewardUnit(award.awardUnit);

  if (award.awardValue != null) {
    // HAP lưu dạng VNĐ ở BE → quy đổi ngược về HAP để hiển thị (15.000 → 15)
    return unit === REWARD_UNIT.HAP ? award.awardValue / HAP_TO_VND : award.awardValue;
  }

  if (unit === REWARD_UNIT.PER_PRODUCT) {
    const minItems = Number(item.minItems);
    const total = Number(award.awardAmount);
    if (minItems > 0 && total > 0) return Math.round(total / minItems);
  }

  return award.awardAmount;
}

/** Sắp xếp awards theo position / minItems để index khớp giữa config gói và config SP */
function sortAwardsForMapping(awards) {
  return [...(awards || [])].sort((a, b) => {
    const posA = a.award?.position ?? a.minItems ?? 0;
    const posB = b.award?.position ?? b.minItems ?? 0;
    if (posA !== posB) return posA - posB;
    return (a.minItems ?? 0) - (b.minItems ?? 0);
  });
}

/** BE config.awards[] → FE milestones[] */
export function mapAwardsToMilestones(awards) {
  return sortAwardsForMapping(awards).map((item, index) => ({
    id: milestoneIdAt(index),
    position: item.award?.position ?? index,
    minQty: item.minItems,
    maxQty: item.maxItems,
    rewardAmount: mapBeAwardToRewardAmount(item),
    rewardUnit: mapAwardUnitToRewardUnit(item.award?.awardUnit),
    displayPercent: item.award?.displayPosition,
    awardAmountTitle: item.award?.awardAmountTitle ?? null,
  }));
}

function resolveMilestoneForAward(milestones, item, index) {
  if (milestones[index]) return milestones[index];

  const position = item.award?.position;
  if (position != null) {
    const byPosition = milestones.find((m) => m.position === position);
    if (byPosition) return byPosition;
  }

  const byMinQty = milestones.find((m) => m.minQty === item.minItems);
  return byMinQty ?? null;
}

function mapProductAwardOverrides(configAwards, milestones) {
  return sortAwardsForMapping(configAwards)
    .map((item, index) => {
      const milestone = resolveMilestoneForAward(milestones, item, index);
      if (!milestone?.id) return null;
      return {
        milestoneId: milestone.id,
        rewardAmount: mapBeAwardToRewardAmount(item),
        rewardUnit: mapAwardUnitToRewardUnit(item.award?.awardUnit),
      };
    })
    .filter(Boolean);
}

/** BE products[] → periodProducts[] (cho mapPeriodProductsToForm) */
export function mapBePeriodProductsToPeriodProducts(beProducts, milestones) {
  return (beProducts || []).map((pp) => ({
    id: pp.productId,
    product: pp.product
      ? {
          id: pp.product.id ?? pp.productId,
          name: pp.product.name,
          code: pp.product.code,
          imageURL: pp.product.imageURL,
        }
      : { id: pp.productId },
    milestones: mapProductAwardOverrides(pp.config?.awards, milestones),
    /** Mốc chiết khấu riêng từng SP (BE: products[].config.awards) — dùng hiển thị detail */
    productAwards: mapAwardsToMilestones(pp.config?.awards),
  }));
}

/** BE GetPeriod → shape hook/UI (milestones, periodProducts, applyScope) */
export function mapGetPeriodResponse(data) {
  const raw = data?.loyaltyPackage?.getPeriod;
  if (!raw) return null;

  const milestones = mapAwardsToMilestones(raw.config?.awards);
  const base = mapPeriodFromBe(raw);

  return {
    ...base,
    milestones,
    numOfMilestones: milestones.length,
    periodProducts: mapBePeriodProductsToPeriodProducts(raw.products, milestones),
  };
}

/** BE GetPeriod (detail) → shape hook/UI — lightweight, fields subset */
export function mapGetPeriodDetailResponse(data) {
  const raw = data?.loyaltyPackage?.getPeriod;
  if (!raw) return null;

  const milestones = mapAwardsToMilestones(raw.config?.awards);

  return {
    id: raw.id,
    title: raw.title,
    startTime: raw.startTime,
    endTime: raw.endTime,
    status: computeLoyaltyStatus(raw.startTime, raw.endTime, raw.resultStatus),
    category: raw.category,
    milestones,
    numOfMilestones: milestones.length,
    periodProducts: mapBePeriodProductsToPeriodProducts(raw.products, milestones),
  };
}

/** BE LoyaltyProductPackageType → FE applyScope */
function mapProductPackageTypeToApplyScope(productPackageType) {
  if (productPackageType === "ONE_PRODUCT") return LOYALTY_APPLY_SCOPE.SINGLE_PRODUCT;
  if (productPackageType === "MULTI_PRODUCT") return LOYALTY_APPLY_SCOPE.MULTIPLE_PRODUCTS;
  return productPackageType;
}

/** BE LoyaltyPackagePeriod → shape UI */
export function mapPeriodFromBe(period) {
  if (!period) return period;

  return {
    ...period,
    applyScope: period.applyScope ?? mapProductPackageTypeToApplyScope(period.productPackageType),
    status: period.status ?? computeLoyaltyStatus(period.startTime, period.endTime, period.resultStatus),
  };
}

/** Map filters FE (UI) → BE ListLoyaltyPackagePeriodsFilters */
export function mapPeriodListFilters(filters) {
  if (!filters) return {};

  const result = {};

  if (filters.query) result.query = filters.query;
  else if (filters.title) result.query = filters.title;

  if (filters.loyaltyPackageId) {
    result.loyaltyPackageId = filters.loyaltyPackageId;
  } else if (filters.loyaltyPackageIds?.length === 1) {
    result.loyaltyPackageId = filters.loyaltyPackageIds[0];
  } else if (filters.loyaltyPackageIds?.length > 1) {
    result.loyaltyPackageIds = filters.loyaltyPackageIds;
  }

  const timeRange = filters.timeFilter || filters.periodTimeRange || filters.timeRange;
  if (timeRange?.from || timeRange?.to) {
    result.timeFilter = { from: timeRange.from, to: timeRange.to };
  }

  if (filters.periodType) result.periodType = filters.periodType;
  if (filters.categoryId) result.categoryId = filters.categoryId;
  if (filters.isActive !== undefined) result.isActive = filters.isActive;

  return result;
}

/** BE ListPeriods → shape hook/UI (`loyaltyPackagePeriods`, `paginationData`) */
export function mapListPeriodsResponse(data) {
  const raw = data?.loyaltyPackage?.listPeriods;
  if (!raw) return null;

  const loyaltyPackagePeriods = (raw.periods || []).map(mapPeriodFromBe);

  return {
    paginationData: raw.paginationData,
    loyaltyPackagePeriods,
  };
}

/** Map filters FE (UI) → BE ListLoyaltyPackagesFilters (BE hỗ trợ `query`, `isActive`) */
export function mapProgramListFilters(filters) {
  if (!filters) return {};

  const result = {};

  if (filters.query) result.query = filters.query;
  else if (filters.title) result.query = filters.title;

  if (filters.isActive !== undefined) result.isActive = filters.isActive;

  return result;
}

/** Map payload form (termsUrl) → BE Create/UpdateLoyaltyPackageInput (policy) */
export function mapProgramInputToBe(input) {
  if (!input) return input;

  const { termsUrl, ...rest } = input;
  const beInput = { ...rest };

  if (termsUrl != null && termsUrl !== "") {
    beInput.policy = termsUrl;
  }

  return beInput;
}

/** BE LoyaltyPackage → shape UI (termsUrl, status fallback) */
export function mapProgramPackageFromBe(item) {
  if (!item) return item;

  return {
    ...item,
    termsUrl: item.termsUrl ?? item.policy ?? "",
    status: item.status ?? computeLoyaltyStatus(item.startTime, item.endTime, item.resultStatus),
  };
}

/** Gộp program (FE/mutation response) vào item cache BE của GetPackageList */
export function mapProgramToListCacheItem(program, existing) {
  if (!program) return existing;

  return {
    ...(existing ?? {}),
    id: program.id,
    title: program.title,
    startTime: program.startTime,
    endTime: program.endTime,
    policy: program.policy ?? program.termsUrl ?? existing?.policy ?? "",
    status: program.status ?? existing?.status,
    isActive: program.isActive ?? existing?.isActive,
    numOfPeriods: program.numOfPeriods ?? existing?.numOfPeriods,
    createdAt: program.createdAt ?? existing?.createdAt,
    updatedAt: program.updatedAt ?? existing?.updatedAt,
    createdBy: program.createdBy ?? existing?.createdBy ?? null,
  };
}

/** BE GetPackageList → shape hook/UI (`loyaltyPackages`, `paginationData`) */
export function mapGetPackageListResponse(data) {
  const raw = data?.loyaltyPackage?.listPackages;
  if (!raw) return null;

  return {
    paginationData: raw.paginationData,
    loyaltyPackages: (raw.packages || []).map(mapProgramPackageFromBe),
  };
}

/** BE GetPackage → shape hook/UI (single program) */
export function mapGetPackageResponse(data) {
  const item = data?.loyaltyPackage?.getPackage;
  if (!item) return null;
  return mapProgramPackageFromBe(item);
}

export function mapCreatePackageResponse(data) {
  return mapProgramPackageFromBe(data?.loyaltyPackage?.createPackage);
}

export function mapUpdatePackageResponse(data) {
  return mapProgramPackageFromBe(data?.loyaltyPackage?.updatePackage);
}

export function mapSwitchPackageStatusResponse(data) {
  return mapProgramPackageFromBe(data?.loyaltyPackage?.switchPackageStatus);
}

/** FE rewardUnit → BE LoyaltyPackageAwardUnit */
export function mapRewardUnitToAwardUnit(rewardUnit) {
  if (rewardUnit === REWARD_UNIT.PER_PRODUCT) return "MONEY_PER_ITEM";
  if (rewardUnit === REWARD_UNIT.PER_MILESTONE) return "MONEY_PER_AWARD";
  if (rewardUnit === REWARD_UNIT.HAP) return "LOYALTY_POINT";
  if (rewardUnit === REWARD_UNIT.GIFT_PER_AWARD) return "GIFT_PER_AWARD";
  return rewardUnit;
}

/** FE applyScope → BE LoyaltyProductPackageType */
export function mapApplyScopeToProductPackageType(applyScope) {
  if (applyScope === LOYALTY_APPLY_SCOPE.SINGLE_PRODUCT) return "ONE_PRODUCT";
  if (applyScope === LOYALTY_APPLY_SCOPE.MULTIPLE_PRODUCTS) return "MULTI_PRODUCT";
  return applyScope;
}

/**
 * Form `rewardAmount` = giá trị mốc theo đơn vị (Tiền/item → chiết khấu/sp).
 * BE create: `awardValue` = `rewardAmount` khi `MONEY_PER_ITEM`.
 */
export { mapRewardAmountToAwardValue } from "../core/utils";

/** Một mốc form → LoyaltyPackageAwardInput */
export function mapMilestoneToAwardInput(milestone, productMilestoneOverride) {
  const rewardAmount = productMilestoneOverride?.rewardAmount ?? milestone.rewardAmount;
  const rewardUnit = productMilestoneOverride?.rewardUnit ?? milestone.rewardUnit;
  const minQty = Number(milestone.minQty);

  // HAP quy đổi sang VNĐ khi gửi BE (15 HAP → 15.000) để BE SUM tổng tiền; đơn vị tiền khác giữ nguyên.
  const awardValue = mapRewardAmountToAwardValue(rewardAmount);
  const awardValueForBe = rewardUnit === REWARD_UNIT.HAP ? awardValue * HAP_TO_VND : awardValue;

  // awardAmountTitle: ưu tiên custom title từ form, fallback format tự động
  const customTitle = productMilestoneOverride?.awardAmountTitle ?? milestone.awardAmountTitle;

  return {
    displayPosition: Number(milestone.displayPercent),
    minItems: minQty,
    maxItems: milestone.maxQty ?? null,
    awardValue: awardValueForBe,
    awardUnit: mapRewardUnitToAwardUnit(rewardUnit),
    awardAmountTitle: customTitle || formatRewardAmount(rewardAmount),
  };
}

/**
 * Payload từ `buildPeriodInput` → BE CreateLoyaltyPackagePeriodInput.
 * config.awards = mốc chung section B (không override SP).
 * products[].config.awards = mốc chung + override từng SP.
 */
export function mapPeriodInputToBe(input) {
  if (!input) return input;

  const { milestones = [], products = [], applyScope, ...rest } = input;
  const productPackageType = mapApplyScopeToProductPackageType(applyScope);

  const mapCommonAwards = () => milestones.map((milestone) => mapMilestoneToAwardInput(milestone));

  const mapAwardsForProduct = (product) =>
    milestones.map((milestone, index) => mapMilestoneToAwardInput(milestone, product?.milestones?.[index]));

  const periodAwards = mapCommonAwards();

  const beProducts = products.map((product) => ({
    productId: product.productId,
    config: {
      awards: mapAwardsForProduct(product),
    },
  }));

  return {
    loyaltyPackageId: rest.loyaltyPackageId,
    title: rest.title,
    startTime: rest.startTime,
    endTime: rest.endTime,
    timeInterval: rest.timeInterval,
    categoryId: rest.categoryId,
    productPackageType,
    config: {
      awards: periodAwards,
    },
    products: beProducts,
  };
}

/**
 * Payload FE → BE UpdateLoyaltyPackagePeriodInput.
 * Khác với createPeriod: KHÔNG gửi loyaltyPackageId (không có trong input type).
 */
export function mapUpdatePeriodInputToBe(input) {
  if (!input) return input;

  const { milestones = [], products = [], applyScope, ...rest } = input;
  const productPackageType = mapApplyScopeToProductPackageType(applyScope);

  const mapCommonAwards = () => milestones.map((milestone) => mapMilestoneToAwardInput(milestone));

  const mapAwardsForProduct = (product) =>
    milestones.map((milestone, index) => mapMilestoneToAwardInput(milestone, product?.milestones?.[index]));

  const periodAwards = mapCommonAwards();

  const beProducts = products.map((product) => ({
    productId: product.productId,
    config: {
      awards: mapAwardsForProduct(product),
    },
  }));

  return {
    title: rest.title,
    startTime: rest.startTime,
    endTime: rest.endTime,
    timeInterval: rest.timeInterval,
    categoryId: rest.categoryId,
    productPackageType,
    config: {
      awards: periodAwards,
    },
    products: beProducts,
  };
}

/** BE createPeriod → shape UI */
export function mapCreatePeriodResponse(data) {
  const raw = data?.loyaltyPackage?.createPeriod;
  if (!raw) return null;
  return mapPeriodFromBe(raw);
}

/** BE updatePeriod → shape UI */
export function mapUpdatePeriodResponse(data) {
  const raw = data?.loyaltyPackage?.updatePeriod;
  if (!raw) return null;
  return mapPeriodFromBe(raw);
}

/** BE switchPeriodStatus → shape UI */
export function mapSwitchPeriodStatusResponse(data) {
  const raw = data?.loyaltyPackage?.switchPeriodStatus;
  if (!raw) return null;
  return mapPeriodFromBe(raw);
}
